import { deriveMonthlyRmBlock } from "@/utils/npdCalc";
import { formatINR } from "@/utils/formatINR";
import { AlertTriangle, ShoppingCart } from "lucide-react";
import DecimalInput from "@/components/DecimalInput";

/**
 * R31 — Sourcing & MOQ block rendered at the foot of Section A on NpdForm.
 *
 * Reads `formData` + `materials` (for the per-material threshold), computes
 * peak-month RM requirement live, and exposes MOQ + supplier confirmation
 * inputs. Only renders the MOQ details when the low-volume flag fires OR
 * the engineer has already typed an MOQ value.
 */
export default function SourcingBlock({ formData, materials, canEdit, updateField, calc }) {
  const material = materials.find((m) => m.material_id === formData.material_id);
  const d = deriveMonthlyRmBlock(formData, material);
  const flag = d.low_volume_flag;
  const block = d.moq_block;
  // Sourcing & MOQ section + checkbox always render — independent of low-volume
  // classification, gross weight, or volumes. Only the amber warning stays gated on `flag`.
  const showMoq = true;
  // R32 — Client-side mirror of backend double-count detection so the warning
  // appears live while typing, not just after save. Backend remains source of truth.
  const _INV_KW = ["inventory", "carrying", "holding", "working capital"];
  const localDoubleCount =
    !!formData.include_carrying_in_price &&
    !!block?.per_piece_carrying_cost &&
    (formData.overheads || []).some((r) =>
      _INV_KW.some((kw) => (r.label || "").toLowerCase().includes(kw)),
    );
  const showDoubleCountWarning =
    (calc?.carrying_double_count_warning || localDoubleCount) && !!formData.include_carrying_in_price;

  return (
    <div
      className="col-span-full mt-4 border-t border-slate-200 pt-3"
      data-testid="rm-sourcing-block"
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
        <div>
          <div className="text-xs text-slate-500">
            Peak-Month RM Requirement
            {d.peak_year_label ? <span className="text-slate-400"> ({d.peak_year_label})</span> : null}
          </div>
          <div className="text-lg font-semibold text-slate-900" data-testid="monthly-rm-kg">
            {d.monthly_rm_kg > 0
              ? `${d.monthly_rm_kg.toFixed(2)} kg/month`
              : <span className="text-slate-400">—</span>}
          </div>
        </div>
        <div>
          <div className="text-xs text-slate-500">Material Threshold</div>
          <div className="text-sm text-slate-700">{d.threshold_kg.toFixed(0)} kg/month</div>
        </div>
        <div>
          <div className="text-xs text-slate-500">Peak Annual Volume</div>
          <div className="text-sm text-slate-700">
            {d.peak_annual_volume_pcs > 0
              ? d.peak_annual_volume_pcs.toLocaleString("en-IN") + " pcs"
              : <span className="text-slate-400">—</span>}
          </div>
        </div>
      </div>

      {flag && !(formData.project_id && formData.moq_covered_by_siblings) && (
        <div
          className="mt-3 rounded-md border border-amber-400 bg-amber-50 p-3 text-sm text-amber-900 flex items-start gap-2"
          data-testid="low-volume-flag"
        >
          <AlertTriangle size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <strong>Low monthly RM requirement.</strong>{" "}
            {d.monthly_rm_kg.toFixed(0)} kg/month is below this material's sourcing threshold
            ({d.threshold_kg.toFixed(0)} kg/month). Capture an MOQ commitment from the supplier
            before approval.
          </div>
        </div>
      )}
      {flag && formData.project_id && formData.moq_covered_by_siblings && (
        <div
          className="mt-3 rounded-md border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-800"
          data-testid="moq-covered-note"
        >
          <strong>MOQ covered by other parts.</strong>{" "}
          This part is below the {d.threshold_kg.toFixed(0)} kg/month threshold on its own, but the
          same material (thickness &amp; grade) is used by other parts in this project that cover the MOQ.
        </div>
      )}
      <label className="mt-2 flex items-start gap-2 text-sm text-slate-700" data-testid="moq-covered-by-siblings">
        <input
          type="checkbox"
          checked={!!formData.moq_covered_by_siblings}
          onChange={(e) => updateField("moq_covered_by_siblings", e.target.checked)}
          disabled={!canEdit}
          className="mt-0.5 h-4 w-4 rounded border-slate-300 text-orange-600 focus:ring-orange-500"
        />
        <span>MOQ covered by other parts in this project (same thickness &amp; grade)</span>
      </label>
      <label className="mt-2 flex items-start gap-2 text-sm text-slate-700" data-testid="rm-provided-by-customer">
        <input
          type="checkbox"
          checked={!!formData.rm_provided_by_customer}
          onChange={(e) => updateField("rm_provided_by_customer", e.target.checked)}
          disabled={!canEdit}
          className="mt-0.5 h-4 w-4 rounded border-slate-300 text-orange-600 focus:ring-orange-500"
        />
        <span>RM will be provided by Customer</span>
      </label>

      {showMoq && (
        <div
          className="mt-3 border border-slate-200 rounded-md bg-slate-50/50 p-3"
          data-testid="moq-block"
        >
          <div className="flex items-center gap-2 mb-2">
            <ShoppingCart size={14} className="text-slate-500" />
            <span className="text-sm font-semibold text-slate-700">Sourcing & MOQ</span>
            {formData.moq_kg ? (
              <span className="text-[11px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-700">
                MOQ {Number(formData.moq_kg).toFixed(0)} kg
              </span>
            ) : null}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <label className="block">
              <span className="text-xs text-slate-500">MOQ (kg)</span>
              <DecimalInput
                className="input mt-1"
                value={formData.moq_kg ?? 0}
                onChange={(v) => updateField("moq_kg", v === 0 ? null : v)}
                disabled={!canEdit}
                placeholder="e.g. 3000"
                data-testid="moq-kg-input"
              />
            </label>
            <label className="block">
              <span className="text-xs text-slate-500">Confirmed with (supplier / contact)</span>
              <input
                type="text"
                className="input mt-1"
                placeholder="e.g. Sanjay @ SAIL"
                value={formData.moq_confirmed_with || ""}
                onChange={(e) => updateField("moq_confirmed_with", e.target.value || null)}
                disabled={!canEdit}
                data-testid="moq-confirmed-with-input"
              />
            </label>
            <label className="block">
              <span className="text-xs text-slate-500">Inventory Holding (% / yr)</span>
              <DecimalInput
                className="input mt-1"
                value={formData.inventory_holding_pct ?? 0}
                onChange={(v) => updateField("inventory_holding_pct", v)}
                disabled={!canEdit}
                placeholder="e.g. 10"
                data-testid="moq-holding-pct-input"
              />
            </label>
          </div>

          {block && (
            <div
              className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-3 border-t border-slate-200 pt-2 text-xs"
              data-testid="moq-derived"
            >
              <Stat
                label="Customer MOQ (pcs)"
                value={Math.round(block.moq_batch_pcs).toLocaleString("en-IN")}
                emphasis
              />
              <Stat
                label="Months of cover"
                value={block.months_of_cover != null ? block.months_of_cover.toFixed(1) : "—"}
                badge={block.status}
              />
              <Stat label="Avg inventory" value={`${block.avg_inventory_kg.toFixed(0)} kg`} />
              <Stat label="Annual carrying" value={formatINR(block.annual_carrying_cost)} />
              <Stat
                label="Per-piece carrying"
                value={block.per_piece_carrying_cost != null
                  ? formatINR(block.per_piece_carrying_cost)
                  : "—"}
                emphasis
              />
            </div>
          )}
          {block && (
            <p className="text-[11px] italic text-slate-500 pt-2 mt-2 border-t border-slate-200">
              Tip: add the per-piece carrying cost as an overhead row in Section E if you want to
              recover it in the selling price.
            </p>
          )}
          {block && (
            <div className="mt-2 border-t border-slate-200 pt-2" data-testid="include-carrying-wrap">
              <label className="flex items-start gap-2 text-sm" data-testid="include-carrying-in-price">
                <input
                  type="checkbox"
                  checked={!!formData.include_carrying_in_price}
                  onChange={(e) => updateField("include_carrying_in_price", e.target.checked)}
                  disabled={!canEdit}
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 text-orange-600 focus:ring-orange-500"
                />
                <span className="text-slate-700">
                  Include carrying cost in selling price{" "}
                  <span className="text-xs text-slate-500">
                    (auto-adds a row to Section E:{" "}
                    <span className="font-mono text-slate-700">
                      {block.per_piece_carrying_cost != null ? formatINR(block.per_piece_carrying_cost) : "—"}/pc
                    </span>
                    )
                  </span>
                </span>
              </label>
              {/* Surface double-count warning when the live UI or the backend detects it */}
              {showDoubleCountWarning && (
                <div
                  className="mt-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2"
                  data-testid="carrying-double-count-warning"
                >
                  ⚠ An existing overhead row already covers inventory / carrying / holding. The
                  auto-injected carrying cost was suppressed to avoid double-counting. Remove the
                  existing row or uncheck this option.
                </div>
              )}
            </div>
          )}
          {formData.moq_confirmed_at && (
            <p className="text-[11px] text-slate-400 mt-1">
              MOQ first confirmed on {String(formData.moq_confirmed_at).slice(0, 10)}.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, badge, emphasis }) {
  const statusClass =
    badge === "healthy" ? "bg-emerald-100 text-emerald-700"
    : badge === "watch" ? "bg-amber-100 text-amber-700"
    : badge === "excessive" ? "bg-rose-100 text-rose-700"
    : "bg-slate-100 text-slate-700";
  return (
    <div className={emphasis ? "font-semibold" : ""}>
      <div className="text-slate-500">{label}</div>
      <div className="flex items-center gap-1.5">
        <span className="font-mono text-slate-900">{value}</span>
        {badge && (
          <span className={`text-[10px] uppercase px-1 rounded ${statusClass}`}>{badge}</span>
        )}
      </div>
    </div>
  );
}
